import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { catchError, tap } from 'rxjs/operators';
import { PRIMARY_OUTLET, Router, UrlSegment, UrlSegmentGroup, UrlTree } from '@angular/router';
import * as _ from 'lodash';
import { LocalStorageService } from '@services/local-storage.service';
import { ConfigService } from '../services/config.service'; // do not import from services.module.ts
import { ToastrService } from 'ngx-toastr';

@Injectable()
export class SurvaleHttpInterceptor implements HttpInterceptor {
  private static authToken: any;
  private static _clientId: string | null = null;
  // the no connectivity screen is shown only after this many requests got no response within a minute
  // (and none got a response in between), so that a single dropped request (e.g. floating filters)
  // does not take the user away from the page
  private static readonly maxConnectionFailures = 5;
  private static readonly connectionFailuresWindowMs = 60 * 1000;
  private static connectionFailures = 0;
  private static connectionFailuresWindowStart = 0;
  private readonly authTokenKey: string;
  private unAuthorizedRoutesLevel1 = ['authentication'];

  static setClientId(clientId: string): void {
    SurvaleHttpInterceptor._clientId = clientId;
  }

  constructor(
    private router: Router,
    private localStorageService: LocalStorageService,
    private configService: ConfigService,
    private toastr: ToastrService
  ) {
    this.authTokenKey = this.configService.localStorageKeys.AuthToken;
    SurvaleHttpInterceptor.authToken = this.localStorageService.get(this.authTokenKey);
  }

  get authToken() {
    return this.localStorageService.get(this.authTokenKey);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (!request.headers.has('Content-Type') && request.url.indexOf('sftp-upload-file') === -1) {
      request = request.clone({ headers: request.headers.set('Content-Type', 'application/json') });
    }

    SurvaleHttpInterceptor.authToken = this.authToken;
    // if (!SurvaleHttpInterceptor.authToken) {
    //   console.log('SurvaleHttpInterceptor.authToken', SurvaleHttpInterceptor.authToken);
    // }
    let headers = request.headers;
    if (SurvaleHttpInterceptor.authToken) {
      headers = headers.set('x-survale-auth-token', SurvaleHttpInterceptor.authToken);
    }
    if (SurvaleHttpInterceptor._clientId) {
      headers = headers.set('x-survale-client-id', SurvaleHttpInterceptor._clientId);
    }
    const clonedReq = request.clone({ headers });
    const routeToLoginOnErrorAPIs = ['v0/session/validate', 'v0/login-user'];
    return next.handle(clonedReq)
      .pipe(
        tap((event: HttpEvent<any>): void => {
          if (event instanceof HttpResponse) {
            SurvaleHttpInterceptor.connectionFailures = 0;
          }
        }),
        catchError((err, caught: Observable<HttpEvent<any>>) => {
          if (err.status !== 0) {
            // the server responded, so the connection is fine
            SurvaleHttpInterceptor.connectionFailures = 0;
          }
          // console.log('Caught error', err);
          if (_.get(err, 'error.msg', '').startsWith('UPDATE PASSWORD:')) {
            //
            this.localStorageService.set('pwdExpiredMsg', err.error);
            this.router.navigate(['authentication', 'change-password']);
            return of(err as any);
          }
          switch (err.status) {
            case 0:
              console.log('clonedReq.url', clonedReq.url);
              // bad fix for now
              // eslint-disable-next-line max-len
              if (clonedReq.url.indexOf('tag-cloud') === -1 && clonedReq.url.indexOf('api.cloudinary.com') === -1 && clonedReq.url.indexOf('client-env') === -1) {
                const now = Date.now();
                if (SurvaleHttpInterceptor.connectionFailures === 0 ||
                  now - SurvaleHttpInterceptor.connectionFailuresWindowStart > SurvaleHttpInterceptor.connectionFailuresWindowMs) {
                  // first failure, or older failures are more than a minute old: start a new window
                  SurvaleHttpInterceptor.connectionFailures = 0;
                  SurvaleHttpInterceptor.connectionFailuresWindowStart = now;
                }
                SurvaleHttpInterceptor.connectionFailures++;
                console.warn(`No response from server (${SurvaleHttpInterceptor.connectionFailures}/` +
                  `${SurvaleHttpInterceptor.maxConnectionFailures})`, clonedReq.url);
                if (SurvaleHttpInterceptor.connectionFailures >= SurvaleHttpInterceptor.maxConnectionFailures) {
                  SurvaleHttpInterceptor.connectionFailures = 0;
                  this.router.navigate(['authentication', 'no-connectivity'], { skipLocationChange: true });
                }
              }
              break;
            case 401:
              const tree: UrlTree = this.router.parseUrl(this.router.url);
              const currentRoute: string =
                tree.root.children[PRIMARY_OUTLET] && tree.root.children[PRIMARY_OUTLET].segments ?
                  tree.root.children[PRIMARY_OUTLET].segments.join('/') : '/';

              if (currentRoute === '/' || (!currentRoute.startsWith(this.unAuthorizedRoutesLevel1[0]))) {
                // this.router.navigate(['authentication', 'login'], {queryParams: {returnUrl: this.router.url}});
                this.router.navigate(['authentication', 'login']);
              }
              return of(err as any);
            case 403:
              /* Login error numbers are set to 400. Process for this error initially
              if (_.get(err, 'error.msg', '').startsWith('UPDATE PASSWORD:')) {
                //
                this.localStorageService.set('pwdExpiredMsg', err.error);
                this.router.navigate(['authentication', 'change-password']);
                return of(err as any);
              }
               */
              break;
            case 409:
              this.toastr.error(
                _.get(err, 'error.error') || 'Your session has changed. Please refresh the page and try again.',
                'Session Mismatch',
                { timeOut: 8000 }
              );
              break;
            default:
              const isSessionCheckRoute = _.find(routeToLoginOnErrorAPIs, r => clonedReq.url.indexOf(r) > -1);
              if (isSessionCheckRoute) {
                // this.router.navigate(['authentication', 'login'], {queryParams: {returnUrl: this.router.url}});
                this.router.navigate(['authentication', 'login']);
              }
          }
          throw err;
          /*
          if (err instanceof HttpErrorResponse && err.status == 401) {
            const tree: UrlTree = this.router.parseUrl(this.router.url);
            const currentRoute: string =
              tree.root.children[PRIMARY_OUTLET] && tree.root.children[PRIMARY_OUTLET].segments ?
                tree.root.children[PRIMARY_OUTLET].segments.join('/') : '/';

            if (currentRoute === '/' || (!currentRoute.startsWith(this.unAuthorizedRoutesLevel1[0]))) {
              // this.router.navigate(['authentication', 'login'], {queryParams: {returnUrl: this.router.url}});
              this.router.navigate(['authentication', 'login']);
            }
            return of(err as any);
          } else {
            // redirect to login if the API call is for session related stuff and it has failed
            const isSessionCheckRoute = _.find(routeToLoginOnErrorAPIs, r => clonedReq.url.indexOf(r) > -1);
            if (isSessionCheckRoute) {
              // this.router.navigate(['authentication', 'login'], {queryParams: {returnUrl: this.router.url}});
              this.router.navigate(['authentication', 'login']);
            }
          }
          throw err;
          */
        })
      );
  }

  setAuthToken(token) {
    // console.log('setting auth token', token);
    SurvaleHttpInterceptor.authToken = token;
    this.localStorageService.set(this.authTokenKey, SurvaleHttpInterceptor.authToken);
  }
}
